import { ModelBase } from "./ModelBase";

export interface DataSourceBase<TId> extends ModelBase<TId> {
  /**
   * Unique code to distinguish a data source.
   * Will not always be set due to custom data sources.
   */
  code?: DataSourceCode;
  name: string;
  url?: string;
  description?: string;
  type: DataSourceType;
  enabled?: boolean;
  fileImportStatus?: ImportStatus;
  fileImportInstructions?: string;
  syncImportStatus?: ImportStatus;
  syncImportInstructions?: string;
  syncImportParams?: SyncImportParamsBase;
  syncAuthType?: "oauth" | "vezgo";
  image?: string;
  mappedToCode?: DataSourceCode;
  /** used to lookup information and mappings about this datasource on Crypto Compare */
  ccInternalName?: string;
  createdAt?: Date;
  updatedAt?: Date;
  networks?: Pick<DataSourceBase<TId>, "_id">[];
}

export interface SyncImportParamsBase {
  key: ImportParamBase;
  secret?: ImportParamBase;
  passphrase?: ImportParamBase;
}

export interface ImportParamBase {
  text: string;
  order: number;
}

export enum DataSourceType {
  Exchange = "exchange",
  Blockchain = "blockchain",
  Wallet = "wallet",
  Custom = "custom",
  Import = "import",
}

// data source names
export enum DataSourceCode {
  // exchanges
  AmberApp = "amberapp",
  Ascendex = "ascendex",
  Bamboo = "bamboo",
  BTCC = "btcc",
  Binance = "Binance",
  BingX = "bingx",
  Bitaroo = "Bitaroo",
  Bitbns = "Bitbns",
  Bitfinex = "Bitfinex",
  Bitget = "bitget",
  Bitkub = "Bitkub",
  BitMart = "BitMart",
  BitMEX = "Bitmex",
  Bitrue = "bitrue",
  Bitstamp = "Bitstamp",
  BitTrade = "bit_trade",
  BitTrex = "BitTrex",
  Bitunix = "bitunix",
  BlockchainDotCom = "blockchaincom", // The exchange platform
  BlockFi = "BlockFi",
  BlockEarner = "block_earner",
  BTCE = "BTCE",
  BtcMarkets = "BTCMarkets",
  Bybit = "bybit",
  CalebBrown = "caleb_and_brown",
  Celsius = "Celsius",
  Cex = "Cexio",
  Coinbase = "Coinbase",
  CoinbasePro = "coinbase_pro", // previously GDAX
  CoinEx = "CoinEx",
  CoinHarbour = "CoinHarbour",
  CoinJar = "CoinJar",
  CoinJarExchange = "CoinJarExchange",
  Cointree = "Cointree",
  CoinSpot = "CoinSpot",
  CoinStash = "CoinStash",
  CryptoDotCom = "Crypto.com",
  CryptoDotComExchange = "crypto_com_exchange",
  Cryptohopper = "cryptohopper",
  CryptoSpend = "cryptospend",
  DeltaExchange = "delta_spot",
  Degate = "degate",
  DigitalSurge = "DigitalSurge",
  Elbaite = "elbaite",
  Ember = "emberfund",
  EtherDelta = "etherdelta",
  EToro = "etoro",
  Finblox = "finblox",
  FinderEarn = "finder_earn",
  FTX = "FTX",
  Gateio = "Gateio",
  Gemini = "Gemini",
  GPIB = "gpib",
  HardBlock = "hardblock",
  HitBTC = "HitBTC",
  HTX = "huobi",
  IGMarkets = "ig_markets",
  IndependentReserve = "IndependentReserve",
  KinesisMoney = "kinesis_money",
  Kraken = "Kraken",
  KuCoin = "KuCoin",
  Ledn = "ledn",
  LROS = "living_room_of_satoshi",
  Luno = "Luno",
  Mercatox = "mercatox",
  MEXC = "mexc",
  MoonPay = "moonpay",
  Nexo = "nexo",
  NexoPro = "nexo-pro",
  NiceHash = "NiceHash",
  OKX = "OKEX",
  Paxful = "paxful",
  Phemex = "phemex",
  Pionex = "pionex",
  Poloniex = "Poloniex",
  PrimeXBT = "primexbt",
  SafeTrade = "SafeCoin",
  SouthXChange = "south_xchange",
  Swyftx = "Swyftx",
  TradeOgre = "trade_ogre",
  Uphold = "uphold",
  Virgo = "virgo",
  Wealth99 = "wealth99",
  WooX = "wootrade",
  Xeggex = "xeggex",
  XTDotCom = "xtpub",
  YieldApp = "yield_app",
  YouHodler = "you_hodler",
  CoinList = "coinlist",
  CoinListPro = "coinlist-pro",
  RelayPay = "relay-pay",
  Deribit = "deribit",
  Zerocap = "zerocap",
  Strike = "strike",
  Stormrake = "stormrake",
  Whitebit = "whitebit",
  Coinstore = "coinstore",
  Revolut = "revolut",
  UpTrade = "uptrade",
  Lbank = "LBank",
  PocketBits = "pocketbits",

  // wallets
  Mintscan = "mintscan",
  Qoin = "qoin",
  Vera = "Vera",
  GalaxyStation = "galaxy-station",
  TerraStation = "terra-station",
  NeonWallet = "neon_wallet",

  // bitcoin wallets
  BlueWallet = "blue-wallet",
  SparrowWallet = "sparrow-wallet",
  Electrum = "electrum",
  Mycelium = "mycelium",
  ColdCard = "coldcard",

  // multi-chain wallets
  Ledger = "Ledger",
  Trezor = "trezor",
  TrustWallet = "trust_wallet",
  AtomicWallet = "atomic_wallet",
  Exodus = "Exodus",
  Bitbox = "bitbox",
  MyEtherWallet = "MyEtherWallet",
  RabbyWallet = "rabby_wallet",
  CoinbaseWallet = "coinbase_wallet",
  Coinomi = "coinomi",
  Jaxx = "jaxx_liberty",
  Bifrost = "bifrost_wallet",
  Guarda = "guarda_wallet",
  BitgetWallet = "bitget-wallet",
  Tangem = "tangem",
  Vultisig = "vultisig",
  SafepalWallet = "safepal_wallet",
  Enkrypt = "enkrypt",
  CryptoComWallet = "crypto-com-wallet", // OnChain wallet previously called crypto.com defi wallet
  BinanceWallet = "binance-wallet",
  OkxWallet = "okx-wallet",
  KeepKey = "keepkey",
  BlockchainDotComWallet = "blockchaincom-wallet", // The non-custodial wallet product

  // evm wallets
  Metamask = "Metamask",
  Etherscan = "Etherscan",
  Rainbow = "rainbow_wallet",
  OneInchWallet = "oneinch_wallet",
  InternetMoney = "internet-money", // for PulseChain

  // ripple wallets
  Xaman = "xaman", // previously XUMM
  ToastWallet = "toast_wallet",
  RippexWallet = "rippex_wallet",

  // stellar wallets
  StellarPort = "stellarport",
  RabetWallet = "rabet_wallet",
  xBullWallet = "xbull_wallet",
  Lobstr = "lobstr",

  // solana wallets
  Solflare = "solflare",
  Phantom = "phantom",

  // cardano wallets
  Daedalus = "daedalus",
  Yoroi = "yoroi",
  Nami = "nami",
  Adalite = "adalite",
  BeginWallet = "begin_wallet",
  Typhon = "typhon",
  Vespr = "vespr",
  Lace = "lace",
  Eternl = "eternl",
  Flint = "flint",
  GeroWallet = "gerowallet",

  // blockchains
  Algorand = "algorand",
  AvalancheXChain = "avalanche-x-chain", // x-chain
  BloFin = "blofin",
  CasperNetwork = "casper-network",
  Elastos = "elastos", // main chain
  Vechain = "vechain",
  Cardano = "Cardano",
  Oraichain = "oraichain",
  Flare = "flare",
  Flow = "flow",
  HederaHashgraph = "hedera-hashgraph", // hbar
  Harmony = "harmony",
  Helium = "Helium",
  Kaspa = "kasplex",
  ICON = "icon",
  InternetComputer = "internet-computer",
  EnergyWeb = "energy-web",
  NearProtocol = "near-protocol",
  MinaProtocol = "mina-protocol",
  MultiversX = "multiversx", // elrond / egld
  SeiNetwork = "sei-network",
  Solana = "solana",
  Songbird = "songbird",
  Stacks = "stacks",
  Stellar = "stellar",
  Sui = "sui",
  Symbol = "symbol",
  Tezos = "tezos",
  NeoLegacy = "neo", // original legacy chain
  NeoN3 = "neo-n3", // new NEO N3 mainchain
  Zilliqa = "zilliqa",
  Ton = "ton",
  Thorchain = "thorchain",
  Tron = "tron",
  XRP = "xrp",
  Nano = "nano",
  Eos = "eos",
  Theta = "theta-network",
  Terra = "terra-two", // the new luna terra chain
  TerraClassic = "terra",
  Cosmos = "cosmos", // Cosmos Hub blockchain
  Agoric = "agoric", // BLD
  Akash = "akash",
  Althea = "althea",
  Archway = "archway",
  Atomone = "atomone",
  Axelar = "axelar",
  Band = "band",
  Bitsong = "bitsong",
  Celestia = "celestia",
  Chihuahua = "chihuahua",
  Coreum = "coreum",
  CronosPos = "crypto-org", // this is the original crypto.com / crypto.org chain
  DyDxChain = "dydx-chain", // dydx v4 on cosmos
  Dymension = "dymension",
  Evmos = "evmos",
  FetchAi = "fetchai", // ASI ALLIANCE
  Finschia = "finschia",
  Govgen = "govgen",
  GravityBridge = "gravity-bridge",
  HumansAi = "humans",
  Injective = "injective-chain",
  Iris = "iris",
  Kava = "kava",
  Mantra = "mantra",
  Medibloc = "medibloc",
  Milkyway = "milkyway",
  Neutron = "neutron",
  Noble = "noble",
  Omniflix = "omniflix",
  OnomyProtocol = "onomy-protocol",
  Osmosis = "osmosis",
  Persistence = "persistence",
  Quasar = "quasar",
  Regen = "regen",
  Saga = "saga",
  Secret = "secret",
  Sei = "sei",
  Selfchain = "selfchain",
  Sentinel = "sentinel", // DVPN
  Shentu = "shentu",
  Sommelier = "sommelier",
  StaFi = "stafi",
  Stargaze = "stargaze",
  Stride = "stride",
  Xion = "xion",
  Xpla = "xpla",
  Zetachain = "zeta",
  Iotex = "iotex",
  Juno = "juno",
  KyveNetwork = "kyve-network",
  Nolus = "nolus",
  Wax = "wax", // WAX Blockchain
  Ultra = "ultra",
  NEM = "nem", // NEM blockchain
  Monero = "monero",
  Aptos = "aptos",
  BeaconChain = "beacon-chain", // Ethereum Beacon Chain
  Kusama = "kusama",
  KuCoinCommunityChain = "kucoin-community-chain", // kcc
  Beldex = "beldex",
  Hyperliquid = "hyperliquid",
  XprNetwork = "xpr-network",
  WavesBlockchain = "waves", // the actual waves blockchain
  WavesExchange = "wx-network", // waves network / waves exchange (the wallet/ exchange which runs on top of waves blockchain)
  Filecoin = "filecoin",

  // Bitcoin-like chains
  Bitcoin = "Bitcoin",
  BitcoinCash = "bitcoinCash",
  BitcoinSv = "bitcoin-sv",
  ECash = "eCash",
  Litecoin = "litecoin",
  Dash = "dash",
  Dogecoin = "dogecoin",
  Zcash = "zcash", // ZEC
  DigiByte = "digibyte", // DGB
  Groestlcoin = "groestlcoin", // GRS

  // Polkadot and parachains
  Polkadot = "polkadot",
  Acala = "acala",
  Astar = "astar",
  Enjin = "enjin",
  VaraNetwork = "vara-network",

  // EVM chains
  Arbitrum = "arbitrum", // arbitrum one
  Avalanche = "avalanche", // c-chain
  Beam = "beam",
  Blast = "blast",
  Chiliz = "chiliz_chain",
  Cyber = "cyber",
  DefiKingdoms = "defi_kingdoms_chain",
  Base = "base",
  BinanceSmartChain = "binance_smart_chain",
  Celo = "celo",
  Cronos = "cronos_evm",
  Ethereum = "Ethereum",
  Fraxtal = "fraxtal",
  Fantom = "fantom",
  Gnosis = "gnosis_chain",
  Linea = "linea",
  Mantle = "mantle",
  Memecore = "memecore",
  Metis = "metis",
  Mint = "mint_chain",
  Mode = "mode_network",
  Moonbeam = "moonbeam",
  Optimism = "optimism",
  Polygon = "polygon",
  Scroll = "scroll",
  Taiko = "taiko",
  ZkSyncEra = "zksync_era",
  Zora = "zora_chain",
  BnbBeaconChain = "bnb_beacon_chain", // Binance / BNB Beacon Chain
  opBNB = "opbnb",
  RedbellyNetwork = "redbelly-network",
  Morph = "morph-l2",
  Nibiru = "nibiru",
  Wyzth = "wyzth",
  Kroma = "kroma",
  XDC = "xdc-network",
  Xai = "xai",
  WorldChain = "world-chain",
  WemixNetwork = "wemix-network",
  Unichain = "unichain",
  Sophon = "sophon",
  Sonic = "sonic",
  PolygonZkEvm = "polygon-zkevm",
  Moonriver = "moonriver",
  BitTorrent = "bittorrent",
  Berachain = "berachain",
  ArbitrumNova = "arbitrum-nova",
  ApeChain = "apechain",
  Abstract = "abstract",
  Dexalot = "dexalot",
  Teleporter1 = "teleporter-1",
  Teleporter2 = "teleporter-2",
  Coqnet = "coqnet",
  StraitsX = "straitsx",
  Omni = "omni-network",
  Tiltyard = "tiltyard",
  GUNZ = "gunz",
  HighOctane = "high-octane",
  Corn = "corn",
  QChain = "qchain",
  Playdapp = "playdapp",
  SwanChain = "swan-chain",
  UPTN = "uptn",
  Ethernity = "ethernity",
  Funki = "funki",
  Superseed = "superseed",
  Polynomial = "polynomial",
  Swellchain = "swellchain",
  Bitrock = "bitrock",
  Donatuz = "donatuz",
  Lisk = "lisk",
  Ink = "ink",
  Pulsar = "pulsar",
  Lamina1 = "lamina1",
  DOS = "dos-chain",
  Orderly = "orderly-network",
  Kinto = "kinto",
  Playa3ull = "playa3ull",
  Shrapnel = "shrapnel",
  Numbers = "numbers",
  XANAChain = "xana-chain",
  Ancient8 = "ancient8",
  PulseChain = "pulsechain",
  StratisEvm = "stratis-evm",
  IotaEvm = "iota-evm",
  LoopNetwork = "loopnetwork", // this is not loopring
  NeoX = "neo-x", // NEO EVM Sideschain
  Electroneum = "electroneum",
  Degen = "degen",
  XrplEvm = "xrpl-evm", // EVM sidechain for XRP Ledger
  Lumia = "lumia",
  Mixin = "mixin",
  FlowEvm = "evm-on-flow",
  ElastosSmartContractChain = "elastos-smart-contract-chain", // ESC is an EVM sidechain for elastos
  EthereumClassic = "ethereum_classic",
  KardiaChain = "kardiachain",
  GateChain = "gatechain",

  EthereumPow = "ethereumpow",

  // others
  OpeningBalances = "sylaOpeningBalances",
  Koinly = "koinly",
  CoinTracking = "cointracking",
  CoinStats = "coinstats",
}

export enum ImportStatus {
  Off = "off",
  Alpha = "alpha",
  Beta = "beta",
  Gold = "gold",
}

export interface DataSourceSupportedInputs {
  sync: ImportStatus | undefined;
  file: ImportStatus | undefined;
}
export const getDataSourceSupportedInputs = ({
  fileImportStatus,
  syncImportStatus,
}: {
  fileImportStatus?: ImportStatus;
  syncImportStatus?: ImportStatus;
}): DataSourceSupportedInputs => ({
  file: (fileImportStatus ?? "off") === "off" ? undefined : fileImportStatus,
  sync: (syncImportStatus ?? "off") === "off" ? undefined : syncImportStatus,
});
